import isEmpty from 'lodash/isEmpty';
import React, {Component} from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import loadable from '@loadable/component';
import {connect} from 'react-redux';
import {compose} from 'redux';
import classNames from 'classnames';
import withConfig from 'config/withConfig';
import SHLoader from 'common/loader/SHLoader';
import {SEARCH_TYPE} from './search-prop-types';
import {DESTINATION_DATA} from 'destination/destination-prop-types';
import WithSearch from './WithSearch';
import WithParkingNearMe from './WithParkingNearMe';
import SearchLayoutDesktop from './layout/SearchLayoutDesktop';
import SpotList from './spot-list/SpotList';
import SpotListSorter from './spot-list/SpotListSorter';
import SpotListSearchControls from './spot-list/SpotListSearchControls';
import SearchMap from './map/SearchMap';
import SpotListSearchControlsCondensed from './spot-list/SpotListSearchControlsCondensed';
import SpotListMonthlyHelp from './spot-list/SpotListMonthlyHelp';
import SpotListItemMonthly from './spot-list/SpotListItemMonthly';

const DestinationInfoPanel = loadable(() =>
    import('../destination/DestinationInfoPanel')
);
const SpotDetailsMonthly = loadable(() =>
    import('spot-details/SpotDetailsMonthly')
);

class SearchDesktopMonthly extends Component {
    static propTypes = {
        history: PropTypes.shape({
            location: PropTypes.object,
        }),
        sortBy: PropTypes.string,
        starts: PropTypes.string,
        ends: PropTypes.string,
        urlDateFormat: PropTypes.string,
        canonicalUrl: PropTypes.string,
        activeSearchTitle: PropTypes.string,
        city: PropTypes.object,
        destination: PropTypes.shape(DESTINATION_DATA).isRequired,
        spots: PropTypes.arrayOf(PropTypes.object),
        selectedSpotId: PropTypes.number,
        searchType: SEARCH_TYPE.isRequired,
        searchRequestId: PropTypes.string,
        monthlyPhoneNumber: PropTypes.string,
        monthlyPhoneNumberFriendly: PropTypes.string,
        isInfoPanelVisible: PropTypes.bool,
        findParkingNearMe: PropTypes.func,
        onSpotDetailsHidden: PropTypes.func,
        isLowInventoryEnabled: PropTypes.bool,
        isSoldOutEnabled: PropTypes.bool,
        onShowDetailsClick: PropTypes.func.isRequired,
        onChangeSort: PropTypes.func.isRequired,
    };
    static defaultProps = {
        isInfoPanelVisible: true,
    };

    _onChangeSort = ({value}) => {
        this.props.onChangeSort(value);
    };

    render() {
        const {
            sortBy,
            starts,
            ends,
            activeSearchTitle,
            isInfoPanelVisible,
            searchRequestId,
            canonicalUrl,
            urlDateFormat,
            city,
            destination,
            spots,
            selectedSpotId,
            findParkingNearMe,
            onSpotDetailsHidden,
            searchType,
            isLowInventoryEnabled,
            isSoldOutEnabled,
            onShowDetailsClick,
            monthlyPhoneNumber,
            monthlyPhoneNumberFriendly,
        } = this.props;
        const hasDestination = !isEmpty(destination);
        const classes = classNames('Search', 'Search-monthly');
        const numSpots = spots.length;
        const showInfoPanel =
            !selectedSpotId &&
            ((searchType === 'city' && city?.description) ||
                (['destination', 'venue'].includes(searchType) &&
                    destination?.description)) &&
            isInfoPanelVisible;
        const sortSelectItems = [
            {value: 'distance', label: 'Sort by Distance'},
            {value: 'price', label: 'Sort by Price'},
        ];
        const validThrough = moment(starts, moment.ISO_8601)
            .add(1, 'month')
            .format(urlDateFormat);
        const monthlySpots = spots.filter(({selectedRate}) => {
            // ensure spots with either missing selectedRate
            // or those without the proper selectedRate.rule_type are not offered to users
            return selectedRate?.rule_type === 'monthly';
        });

        return (
            <div className={classes}>
                {searchRequestId ? (
                    <SearchLayoutDesktop
                        SearchMap={
                            <SearchMap
                                spots={monthlySpots}
                                findParkingNearMe={findParkingNearMe}
                                isSoldOutEnabled={isSoldOutEnabled}
                                onShowDetailsClick={onShowDetailsClick}
                            />
                        }
                        SpotDetails={
                            <SpotDetailsMonthly
                                spotId={selectedSpotId}
                                onCloseClick={onSpotDetailsHidden}
                                isLowInventoryEnabled={isLowInventoryEnabled}
                            />
                        }
                        SpotList={
                            <SpotList
                                spots={monthlySpots}
                                selectedSpotId={selectedSpotId}
                                canonicalUrl={canonicalUrl}
                                validFrom={starts}
                                validThrough={validThrough}
                                isLowInventoryEnabled={isLowInventoryEnabled}
                                isSoldOutEnabled={isSoldOutEnabled}
                                onShowDetailsClick={onShowDetailsClick}
                                VerticalPromo={
                                    <SpotListMonthlyHelp
                                        monthlyPhoneNumber={monthlyPhoneNumber}
                                        monthlyPhoneNumberFriendly={
                                            monthlyPhoneNumberFriendly
                                        }
                                    />
                                }
                                SpotListItem={SpotListItemMonthly}
                            />
                        }
                        SpotListSorter={
                            <SpotListSorter
                                sortBy={sortBy}
                                sortSelectItems={sortSelectItems}
                                onChangeSort={this._onChangeSort}
                            />
                        }
                        SpotListSearchControls={
                            <SpotListSearchControls numSpots={numSpots} />
                        }
                        SpotListSearchControlsCondensed={
                            <SpotListSearchControlsCondensed
                                starts={starts}
                                ends={ends}
                                title={activeSearchTitle}
                                isMonthly
                            />
                        }
                        DestinationInfoPanel={
                            searchType !== 'address' ? (
                                <DestinationInfoPanel
                                    {...(hasDestination
                                        ? {
                                              title: `${destination.title} Monthly`,
                                              imageId:
                                                  destination.default_image
                                                      ?.object.public_id,
                                              imageAlt:
                                                  destination.default_image
                                                      ?.object.alt_text,
                                              address:
                                                  destination.street_address,
                                              city: destination.city,
                                              state: destination.state,
                                              description:
                                                  destination.monthly_description ||
                                                  destination.description,
                                          }
                                        : {
                                              title: `${city.display_name} Monthly`,
                                              imageId: city.default_image.id,
                                              imageAlt: `${city.display_name} Monthly`,
                                              address: null,
                                              city: city.display_name,
                                              state: city.state,
                                              description:
                                                  city.monthly_description ||
                                                  city.description,
                                          })}
                                />
                            ) : null
                        }
                        spots={spots}
                        selectedSpotId={selectedSpotId}
                        showInfoPanel={showInfoPanel}
                        disableKeydownEvents={isSoldOutEnabled}
                    />
                ) : (
                    <SHLoader />
                )}
            </div>
        );
    }
}

const mapStateToProps = ({
    city: {data: city},
    destination: {data: destination},
    search: {
        data: {
            breadcrumbs: {
                activeBreadcrumb: {label: activeSearchTitle},
            },
            isInfoPanelVisible,
            searchType,
        },
    },
    searchRequest: {id: searchRequestId},
}) => {
    const {
        monthly_phone_number: monthlyPhoneNumber,
        monthly_phone_number_friendly: monthlyPhoneNumberFriendly,
    } = city;

    return {
        city,
        destination,
        searchType,
        activeSearchTitle,
        isInfoPanelVisible,
        searchRequestId,
        monthlyPhoneNumber,
        monthlyPhoneNumberFriendly,
    };
};

const enhance = compose(
    withConfig(['urlDateFormat']),
    WithSearch,
    WithParkingNearMe,
    connect(mapStateToProps)
);

export default enhance(SearchDesktopMonthly);
